import { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { bauvorhabenApi } from "@/services/api";
import { useAuth } from "@/hooks/useAuth";
import { useAppEvent } from "@/utils/appEvents";
import { computeOnTrack, formatDateOnly } from "@/utils/progress";
import {
  bauvorhabenStatusLabels, bauvorhabenStatusBadgeClasses,
} from "@/components/bauvorhaben/bauvorhabenStatus";
import type { Bauvorhaben, BauvorhabenStatus } from "@/types";
import {
  Search, Plus, HardHat, MapPin, CalendarRange, User, ListChecks,
  Loader2, AlertCircle, ChevronRight,
} from "lucide-react";

type StatusFilter = '' | BauvorhabenStatus;

const statusFilters: Array<{ value: StatusFilter; label: string }> = [
  { value: '', label: 'Alle' },
  { value: 'geplant', label: 'Nicht begonnen' },
  { value: 'aktiv', label: 'In Arbeit' },
  { value: 'abgeschlossen', label: 'Abgeschlossen' },
  { value: 'abgebrochen', label: 'Abgebrochen' },
];

/**
 * Bauvorhaben-Listen-Spalte: Root = eigener Scroll-Container
 * (IntersectionObserver mit root auf diese Spalte). Öffnet Bauvorhaben
 * über die Route in der Detail-Spalte. Bleibt durch ModuleColumns
 * gemountet — Änderungen im Detail erreichen sie über den App-Event-Bus
 * ('bauvorhaben:changed') als stilles Nachladen der Seiten 1..page.
 */
export function BauvorhabenList() {
  const { isApprover } = useAuth();

  const [bauvorhaben, setBauvorhaben] = useState<Bauvorhaben[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('');
  const [bauleiterFilter, setBauleiterFilter] = useState('');
  const [bauleiterOptions, setBauleiterOptions] = useState<{ id: number; name: string }[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState('');
  const scrollRef = useRef<HTMLDivElement>(null);
  const observerTarget = useRef<HTMLDivElement>(null);
  // Sequenznummer gegen Races: Antworten veralteter Reloads (Event-Bus oder
  // Filterwechsel) verwerfen.
  const reloadSeq = useRef(0);

  // Bauleiter-Optionen einmalig beim Mount (nur tatsächlich referenzierte
  // Benutzer; bei Fehlern bleibt das Select ausgeblendet).
  useEffect(() => {
    bauvorhabenApi
      .getBauleiter()
      .then((response) => setBauleiterOptions(response.data.data))
      .catch(() => setBauleiterOptions([]));
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, statusFilter, bauleiterFilter]);

  useEffect(() => {
    let cancelled = false;
    const seq = ++reloadSeq.current;
    setIsLoading(true);
    setError('');
    bauvorhabenApi
      .getAll({ page: 1, search: debouncedSearch, status: statusFilter, bauleiter: bauleiterFilter })
      .then((response) => {
        if (cancelled || seq !== reloadSeq.current) return;
        setBauvorhaben(response.data.data);
        const pagination = response.data.pagination;
        setHasMore(pagination ? pagination.page < pagination.total_pages : false);
      })
      .catch(() => {
        if (!cancelled) setError('Fehler beim Laden der Bauvorhaben');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debouncedSearch, statusFilter, bauleiterFilter]);

  // Stilles Nachladen der aktuell geladenen Seiten (1..page) — Seite bleibt
  // erhalten, kein Spinner. Ausgelöst durch 'bauvorhaben:changed'.
  const silentReload = useCallback(() => {
    const seq = ++reloadSeq.current;
    const requests = [];
    for (let p = 1; p <= page; p++) {
      requests.push(
        bauvorhabenApi.getAll({ page: p, search: debouncedSearch, status: statusFilter, bauleiter: bauleiterFilter })
      );
    }
    Promise.all(requests)
      .then((responses) => {
        if (seq !== reloadSeq.current) return;
        setBauvorhaben(responses.flatMap((r) => r.data.data));
        const last = responses[responses.length - 1];
        const pagination = last.data.pagination;
        setHasMore(pagination ? pagination.page < pagination.total_pages : false);
      })
      .catch(() => {
        // Still scheitern — die nächste Nutzer-Aktion lädt ohnehin neu.
      });
  }, [page, debouncedSearch, statusFilter, bauleiterFilter]);

  useAppEvent('bauvorhaben:changed', silentReload);

  const loadMore = useCallback(() => {
    if (isLoadingMore || !hasMore) return;
    setIsLoadingMore(true);
    const nextPage = page + 1;
    bauvorhabenApi
      .getAll({ page: nextPage, search: debouncedSearch, status: statusFilter, bauleiter: bauleiterFilter })
      .then((response) => {
        setBauvorhaben((prev) => [...prev, ...response.data.data]);
        setHasMore((response.data.pagination?.page ?? nextPage) < (response.data.pagination?.total_pages ?? 0));
        setPage(nextPage);
      })
      .catch(() => setError('Fehler beim Laden der Bauvorhaben'))
      .finally(() => setIsLoadingMore(false));
  }, [isLoadingMore, hasMore, page, debouncedSearch, statusFilter, bauleiterFilter]);

  useEffect(() => {
    const root = scrollRef.current;
    if (!root || !observerTarget.current) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) loadMore();
      },
      { root, threshold: 1 }
    );
    observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [loadMore]);

  return (
    <div ref={scrollRef} className="flex h-full min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="sticky top-0 z-10 bg-gray-50/95 backdrop-blur-sm px-3 pt-3 pb-3 space-y-3">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <HardHat className="h-5 w-5 text-gray-600" />
            <h1 className="text-lg font-bold text-gray-900">Bauvorhaben</h1>
          </div>
          {isApprover && (
            <div className="flex gap-2">
              <Link
                to="/bauvorhaben/gewerke"
                className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50"
                title="Gewerke verwalten"
              >
                <ListChecks className="h-4 w-4" />
                Gewerke
              </Link>
              <Link to="/bauvorhaben/new" className="btn btn-primary inline-flex items-center gap-1.5 px-3 py-1.5 text-sm">
                <Plus className="h-4 w-4" />
                Neu
              </Link>
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Suchen — Name, Projektnummer, Ort…"
              className="input pl-9"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {statusFilters.map((filter) => (
              <button
                key={filter.value}
                onClick={() => setStatusFilter(filter.value)}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${
                  statusFilter === filter.value
                    ? 'bg-primary-600 border-primary-600 text-white'
                    : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {filter.label}
              </button>
            ))}
          </div>
          {bauleiterOptions.length > 0 && (
            <select
              value={bauleiterFilter}
              onChange={(e) => setBauleiterFilter(e.target.value)}
              className="input"
              aria-label="Nach Bauleiter filtern"
            >
              <option value="">Alle Bauleiter</option>
              {bauleiterOptions.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.name}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {error && (
        <div className="mx-3 mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <AlertCircle className="h-4 w-4 shrink-0" />
          {error}
        </div>
      )}

      <div className="px-3 pb-4">
        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary-600" />
          </div>
        ) : bauvorhaben.length === 0 && !error ? (
          <div className="card p-8 text-center text-gray-500">
            <HardHat className="mx-auto mb-3 h-8 w-8 text-gray-400" />
            <p>Keine Bauvorhaben gefunden</p>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {bauvorhaben.map((bvh) => {
              const track = computeOnTrack(bvh);
              const fertig = bvh.gewerke_fertig_count ?? 0;
              const total = bvh.gewerke_total_count ?? 0;
              const progress = bvh.progress_percent ?? 0;
              return (
                <Link
                  key={bvh.id}
                  to={`/bauvorhaben/${bvh.id}`}
                  className="bg-white rounded-lg border border-gray-200 p-3 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <h2 className="font-semibold text-gray-900 leading-snug">{bvh.name}</h2>
                    <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
                  </div>
                  <div className="flex items-center gap-2 flex-wrap mb-2">
                    {bvh.project_number && (
                      <span className="text-xs text-gray-500">Projekt-Nr. {bvh.project_number}</span>
                    )}
                    <span
                      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${bauvorhabenStatusBadgeClasses[bvh.status]}`}
                    >
                      {bauvorhabenStatusLabels[bvh.status]}
                    </span>
                  </div>
                  <div className="space-y-1 text-sm text-gray-600">
                    {(bvh.city || bvh.street) && (
                      <p className="flex items-center gap-1.5">
                        <MapPin className="h-4 w-4 shrink-0 text-gray-400" />
                        {[bvh.street, bvh.postal_code && `${bvh.postal_code} `, bvh.city].filter(Boolean).join(' ')}
                      </p>
                    )}
                    {bvh.bauleiter_name && (
                      <p className="flex items-center gap-1.5">
                        <User className="h-4 w-4 shrink-0 text-gray-400" />
                        Bauleiter: {bvh.bauleiter_name}
                      </p>
                    )}
                    <p className="flex items-center gap-1.5 flex-wrap">
                      <CalendarRange className="h-4 w-4 shrink-0 text-gray-400" />
                      {bvh.end_date
                        ? `Fertigstellung: ${formatDateOnly(bvh.end_date)}`
                        : bvh.start_date
                        ? `ab ${formatDateOnly(bvh.start_date)}`
                        : 'Kein Termin'}
                      {track.kind !== 'none' && (
                        <span className={`rounded-full px-1.5 py-0.5 text-xs font-medium ${track.chipClasses}`}>
                          {track.label}
                        </span>
                      )}
                    </p>
                    <p className="flex items-center gap-1.5">
                      <HardHat className="h-4 w-4 shrink-0 text-gray-400" />
                      {bvh.bauherren_count ?? 0} Bauherren ·{' '}
                      {total > 0 ? `${fertig} von ${total} Gewerken fertig · ${progress} %` : 'Keine Gewerke definiert'}
                    </p>
                  </div>
                  <div className="mt-2 h-1.5 rounded-full bg-gray-200 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-primary-600"
                      style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                    />
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        <div ref={observerTarget} className="h-4" />
        {isLoadingMore && (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-5 w-5 animate-spin text-primary-600" />
          </div>
        )}
      </div>
    </div>
  );
}